import { useState } from 'react';
import { X, ChevronRight, ChevronLeft, Check, Target, Users, Image, Sparkles, RefreshCw } from 'lucide-react';

const BASE_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to Funnel Builder',
        subtitle: 'Let\'s create your first advertising funnel',
        icon: Sparkles,
        description: 'A funnel guides potential customers from awareness to conversion. We\'ll walk you through creating each part.',
        fields: []
    },
    {
        id: 'campaign',
        title: 'Create Your Campaign',
        subtitle: 'Step 1 of 3',
        icon: Target,
        description: 'Campaigns are the top level of your funnel. They define your objective and budget.',
        fields: [
            { name: 'name', label: 'Campaign Name', type: 'text', placeholder: 'e.g., Summer Sale Awareness', required: true },
            { name: 'objective', label: 'Objective', type: 'select', options: [
                { value: 'AWARENESS', label: 'Awareness - Reach new people' },
                { value: 'TRAFFIC', label: 'Traffic - Drive website visits' },
                { value: 'ENGAGEMENT', label: 'Engagement - Get likes and comments' },
                { value: 'LEADS', label: 'Leads - Collect contact information' },
                { value: 'CONVERSIONS', label: 'Conversions - Drive purchases' }
            ], required: true },
            { name: 'budget', label: 'Daily Budget ($)', type: 'number', placeholder: '50', required: true },
            { name: 'funnelStage', label: 'Funnel Stage', type: 'select', options: [
                { value: 'TOFU', label: 'TOFU - Top of Funnel (Awareness)' },
                { value: 'MOFU', label: 'MOFU - Middle of Funnel (Consideration)' },
                { value: 'BOFU', label: 'BOFU - Bottom of Funnel (Conversion)' }
            ], required: true }
        ],
        helpText: 'Choose an objective that matches your marketing goal. The funnel stage determines where this campaign fits in your customer journey.'
    },
    {
        id: 'adset',
        title: 'Define Your Audience',
        subtitle: 'Step 2 of 3',
        icon: Users,
        description: 'Ad Sets define who sees your ads. Set targeting to reach the right people.',
        fields: [
            { name: 'name', label: 'Ad Set Name', type: 'text', placeholder: 'e.g., Women 25-45 Interested in Fashion', required: true },
            { name: 'ageMin', label: 'Minimum Age', type: 'number', placeholder: '18' },
            { name: 'ageMax', label: 'Maximum Age', type: 'number', placeholder: '65' },
            { name: 'locations', label: 'Target Locations', type: 'text', placeholder: 'e.g., United States, Canada' },
            { name: 'interests', label: 'Interests', type: 'text', placeholder: 'e.g., Fashion, Shopping, Lifestyle' }
        ],
        helpText: 'The more specific your targeting, the more relevant your ads will be. Start broad and narrow down based on performance.'
    },
    {
        id: 'ad',
        title: 'Create Your Ad',
        subtitle: 'Step 3 of 3',
        icon: Image,
        description: 'Ads are what your audience actually sees. Create compelling content that drives action.',
        fields: [
            { name: 'name', label: 'Ad Name', type: 'text', placeholder: 'e.g., Summer Sale Video Ad', required: true },
            { name: 'headline', label: 'Headline', type: 'text', placeholder: 'e.g., Summer Sale - Up to 50% Off!' },
            { name: 'primaryText', label: 'Primary Text', type: 'textarea', placeholder: 'Write your ad copy here...' },
            { name: 'cta', label: 'Call to Action', type: 'select', options: [
                { value: 'LEARN_MORE', label: 'Learn More' },
                { value: 'SHOP_NOW', label: 'Shop Now' },
                { value: 'SIGN_UP', label: 'Sign Up' },
                { value: 'BOOK_NOW', label: 'Book Now' },
                { value: 'CONTACT_US', label: 'Contact Us' }
            ], required: true }
        ],
        helpText: 'Strong headlines and clear calls to action drive better results. You can add creative images or videos later.'
    }
];

const RETARGETING_STEP = {
    id: 'retargeting',
    title: 'Set Up Retargeting',
    subtitle: 'Step 4 of 4 (Optional)',
    icon: RefreshCw,
    description: 'Capture users from earlier funnel stages and automatically move them into this campaign.',
    fields: [],
    helpText: 'Retargeting pools automatically collect engaged users and feed them to your campaign, creating an automated audience flow.'
};

export default function FunnelWizard({ isOpen, onClose, onComplete }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({
        campaign: { name: '', objective: 'AWARENESS', budget: 50, funnelStage: 'TOFU' },
        adset: { name: '', ageMin: 18, ageMax: 65, locations: '', interests: '' },
        ad: { name: '', headline: '', primaryText: '', cta: 'LEARN_MORE' },
        retargeting: { enabled: false, sourceStage: 'TOFU', poolType: 'engagement' }
    });

    if (!isOpen) return null;

    // Show retargeting step only for MOFU/BOFU campaigns
    const showRetargetingStep = formData.campaign.funnelStage !== 'TOFU';
    const WIZARD_STEPS = showRetargetingStep ? [...BASE_STEPS, RETARGETING_STEP] : BASE_STEPS;

    const step = WIZARD_STEPS[currentStep];
    const StepIcon = step.icon;

    const getStepDataKey = () => {
        if (currentStep === 0) return null;
        if (currentStep === 1) return 'campaign';
        if (currentStep === 2) return 'adset';
        if (currentStep === 3) return 'ad';
        if (currentStep === 4) return 'retargeting';
        return null;
    };

    const handleFieldChange = (fieldName, value) => {
        const dataKey = getStepDataKey();
        if (!dataKey) return;

        setFormData(prev => ({
            ...prev,
            [dataKey]: {
                ...prev[dataKey],
                [fieldName]: value
            }
        }));
    };

    const canProceed = () => {
        if (currentStep === 0) return true;
        if (currentStep === 4) return true; // Retargeting step is optional
        const dataKey = getStepDataKey();
        const stepData = formData[dataKey];
        const requiredFields = step.fields.filter(f => f.required);
        return requiredFields.every(f => stepData[f.name]);
    };

    const handleNext = () => {
        if (currentStep < WIZARD_STEPS.length - 1) {
            setCurrentStep(prev => prev + 1);
        } else {
            handleComplete();
        }
    };

    const handleBack = () => {
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1);
        }
    };

    const handleComplete = () => {
        const result = {
            campaign: {
                ...formData.campaign,
                budget: { amount: parseFloat(formData.campaign.budget) || 50, type: 'daily' },
                status: 'draft'
            },
            adset: {
                ...formData.adset,
                targeting: {
                    ageMin: parseInt(formData.adset.ageMin) || 18,
                    ageMax: parseInt(formData.adset.ageMax) || 65,
                    locations: formData.adset.locations ? formData.adset.locations.split(',').map(l => l.trim()) : [],
                    interests: formData.adset.interests ? formData.adset.interests.split(',').map(i => i.trim()) : [],
                    genders: []
                },
                status: 'draft'
            },
            ad: {
                ...formData.ad,
                status: 'draft'
            }
        };

        // Include retargeting pool if enabled
        if (formData.retargeting.enabled) {
            result.retargetingPool = {
                name: `${formData.retargeting.sourceStage} ${formData.retargeting.poolType === 'engagement' ? 'Engagers' : formData.retargeting.poolType === 'video_viewer' ? 'Video Viewers' : 'Website Visitors'}`,
                poolType: formData.retargeting.poolType,
                sourceStage: formData.retargeting.sourceStage,
                targetStage: formData.campaign.funnelStage,
                trigger: {
                    type: formData.retargeting.poolType === 'video_viewer' ? 'video_view' :
                          formData.retargeting.poolType === 'website' ? 'website_visit' : 'engagement',
                    threshold: formData.retargeting.poolType === 'video_viewer' ? 50 : 1,
                    lookbackDays: 30
                },
                estimatedSize: 0,
                isActive: true,
                status: 'draft'
            };
        }

        onComplete(result);
        onClose();
    };

    const renderField = (field) => {
        const dataKey = getStepDataKey();
        const value = formData[dataKey]?.[field.name] || '';

        const baseInputStyle = {
            width: '100%',
            padding: 'var(--space-2) var(--space-3)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-surface)',
            color: 'var(--color-text-primary)',
            fontSize: '0.9rem'
        };

        if (field.type === 'select') {
            return (
                <select
                    value={value}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    style={baseInputStyle}
                >
                    {field.options.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            );
        }

        if (field.type === 'textarea') {
            return (
                <textarea
                    value={value}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    placeholder={field.placeholder}
                    rows={3}
                    style={{ ...baseInputStyle, resize: 'vertical' }}
                />
            );
        }

        return (
            <input
                type={field.type}
                value={value}
                onChange={(e) => handleFieldChange(field.name, e.target.value)}
                placeholder={field.placeholder}
                style={baseInputStyle}
            />
        );
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                width: '90%',
                maxWidth: '600px',
                maxHeight: '85vh',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-6)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <div style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: 'var(--primary-100)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            <StepIcon size={20} style={{ color: 'var(--color-primary)' }} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
                                {step.title}
                            </h2>
                            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                {step.subtitle}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Progress bar */}
                <div style={{
                    display: 'flex',
                    padding: '0 var(--space-6)',
                    gap: 'var(--space-2)',
                    marginTop: 'var(--space-4)'
                }}>
                    {WIZARD_STEPS.map((_, idx) => (
                        <div
                            key={idx}
                            style={{
                                flex: 1,
                                height: '4px',
                                borderRadius: '2px',
                                backgroundColor: idx <= currentStep
                                    ? 'var(--color-primary)'
                                    : 'var(--color-border)'
                            }}
                        />
                    ))}
                </div>

                {/* Content */}
                <div style={{
                    flex: 1,
                    overflow: 'auto',
                    padding: 'var(--space-6)'
                }}>
                    <p style={{
                        margin: '0 0 var(--space-4) 0',
                        fontSize: '0.9rem',
                        color: 'var(--color-text-secondary)',
                        lineHeight: 1.6
                    }}>
                        {step.description}
                    </p>

                    {/* Retargeting Step Custom Content */}
                    {step.id === 'retargeting' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            {/* Enable Toggle */}
                            <div style={{
                                display: 'flex',
                                gap: 'var(--space-3)',
                                marginBottom: 'var(--space-2)'
                            }}>
                                <button
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        retargeting: { ...prev.retargeting, enabled: true }
                                    }))}
                                    style={{
                                        flex: 1,
                                        padding: 'var(--space-4)',
                                        border: `2px solid ${formData.retargeting.enabled ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                        borderRadius: 'var(--radius-md)',
                                        backgroundColor: formData.retargeting.enabled ? 'var(--primary-50)' : 'transparent',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                        Yes, add Retargeting Pool
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                        Automatically capture engaged users
                                    </div>
                                </button>
                                <button
                                    onClick={() => setFormData(prev => ({
                                        ...prev,
                                        retargeting: { ...prev.retargeting, enabled: false }
                                    }))}
                                    style={{
                                        flex: 1,
                                        padding: 'var(--space-4)',
                                        border: `2px solid ${!formData.retargeting.enabled ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                        borderRadius: 'var(--radius-md)',
                                        backgroundColor: !formData.retargeting.enabled ? 'var(--primary-50)' : 'transparent',
                                        cursor: 'pointer',
                                        textAlign: 'left'
                                    }}
                                >
                                    <div style={{ fontWeight: 600, marginBottom: 'var(--space-1)' }}>
                                        Skip for now
                                    </div>
                                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
                                        You can add this later
                                    </div>
                                </button>
                            </div>

                            {/* Pool Configuration (shown when enabled) */}
                            {formData.retargeting.enabled && (
                                <div style={{
                                    padding: 'var(--space-4)',
                                    backgroundColor: 'var(--color-bg-secondary)',
                                    borderRadius: 'var(--radius-md)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 'var(--space-4)'
                                }}>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: 'var(--space-1)',
                                            fontSize: '0.85rem',
                                            fontWeight: 500
                                        }}>
                                            Source Stage
                                        </label>
                                        <select
                                            value={formData.retargeting.sourceStage}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                retargeting: { ...prev.retargeting, sourceStage: e.target.value }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: 'var(--space-2) var(--space-3)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                backgroundColor: 'var(--color-surface)',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            {formData.campaign.funnelStage === 'MOFU' && (
                                                <option value="TOFU">TOFU (Awareness)</option>
                                            )}
                                            {formData.campaign.funnelStage === 'BOFU' && (
                                                <>
                                                    <option value="TOFU">TOFU (Awareness)</option>
                                                    <option value="MOFU">MOFU (Consideration)</option>
                                                </>
                                            )}
                                        </select>
                                        <p style={{ margin: 'var(--space-1) 0 0', fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                                            Capture users who engaged at this stage
                                        </p>
                                    </div>

                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: 'var(--space-1)',
                                            fontSize: '0.85rem',
                                            fontWeight: 500
                                        }}>
                                            Pool Type
                                        </label>
                                        <select
                                            value={formData.retargeting.poolType}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                retargeting: { ...prev.retargeting, poolType: e.target.value }
                                            }))}
                                            style={{
                                                width: '100%',
                                                padding: 'var(--space-2) var(--space-3)',
                                                border: '1px solid var(--color-border)',
                                                borderRadius: 'var(--radius-md)',
                                                backgroundColor: 'var(--color-surface)',
                                                fontSize: '0.9rem'
                                            }}
                                        >
                                            <option value="engagement">Engagers - Any ad interaction</option>
                                            <option value="video_viewer">Video Viewers - Watched 50%+</option>
                                            <option value="website">Website Visitors - Visited your site</option>
                                        </select>
                                        <p style={{ margin: 'var(--space-1) 0 0', fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>
                                            What behavior should trigger adding users to this pool
                                        </p>
                                    </div>

                                    {/* Preview */}
                                    <div style={{
                                        padding: 'var(--space-3)',
                                        backgroundColor: 'var(--color-surface)',
                                        borderRadius: 'var(--radius-sm)',
                                        border: '1px dashed var(--color-border)'
                                    }}>
                                        <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-1)' }}>
                                            Flow Preview
                                        </div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                            {formData.retargeting.sourceStage} → Pool → {formData.campaign.funnelStage}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Standard Fields */}
                    {step.id !== 'retargeting' && step.fields.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                            {step.fields.map(field => (
                                <div key={field.name}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: 'var(--space-1)',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        color: 'var(--color-text-primary)'
                                    }}>
                                        {field.label}
                                        {field.required && <span style={{ color: 'var(--color-error)' }}> *</span>}
                                    </label>
                                    {renderField(field)}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Help text */}
                    {step.helpText && (
                        <div style={{
                            marginTop: 'var(--space-4)',
                            padding: 'var(--space-3)',
                            backgroundColor: 'var(--primary-50)',
                            borderRadius: 'var(--radius-md)',
                            fontSize: '0.8rem',
                            color: 'var(--primary-700)',
                            lineHeight: 1.5
                        }}>
                            <strong>Tip:</strong> {step.helpText}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-6)',
                    borderTop: '1px solid var(--color-border)'
                }}>
                    <button
                        onClick={handleBack}
                        disabled={currentStep === 0}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-2) var(--space-4)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: 'transparent',
                            color: currentStep === 0 ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
                            cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem'
                        }}
                    >
                        <ChevronLeft size={16} />
                        Back
                    </button>

                    <button
                        onClick={handleNext}
                        disabled={!canProceed()}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-1)',
                            padding: 'var(--space-2) var(--space-4)',
                            border: 'none',
                            borderRadius: 'var(--radius-md)',
                            backgroundColor: canProceed() ? 'var(--color-primary)' : 'var(--color-border)',
                            color: canProceed() ? 'white' : 'var(--color-text-tertiary)',
                            cursor: canProceed() ? 'pointer' : 'not-allowed',
                            fontSize: '0.9rem',
                            fontWeight: 500
                        }}
                    >
                        {currentStep === WIZARD_STEPS.length - 1 ? (
                            <>
                                <Check size={16} />
                                Create Funnel
                            </>
                        ) : (
                            <>
                                Next
                                <ChevronRight size={16} />
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
